import { _firestore } from '../firebase';

const LOG_PREFIX = '[OrderThrottling]';

const TIME_REGEX = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;

const twelveHourToSeconds = (str) => {
  if (!str || typeof str !== 'string') return null;
  const match = TIME_REGEX.exec(str.trim());
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  return hours * 3600 + minutes * 60;
};

const secondsToHHMM = (secs) => {
  if (!Number.isFinite(secs)) return String(secs);
  const h = Math.floor(secs / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((secs % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const isSlotFull = (slot) => {
  const maxOrders = Number(slot.maxOrders);
  const orders = Number(slot.orders) || 0;
  const maxVolume = Number(slot.orderVolume);
  const volume = Number(slot.volume) || 0;
  const type = String(slot.type);
  if (type === '0') {
    return Number.isFinite(maxOrders) && maxOrders > 0 && orders >= maxOrders;
  }
  if (type === '1') {
    return Number.isFinite(maxVolume) && maxVolume > 0 && volume >= maxVolume;
  }
  if (type === '2') {
    const ordersCapped =
      Number.isFinite(maxOrders) && maxOrders > 0 && orders >= maxOrders;
    const volumeCapped =
      Number.isFinite(maxVolume) && maxVolume > 0 && volume >= maxVolume;
    return ordersCapped && volumeCapped;
  }
  return false;
};

const filterDay = async (dayObj, storeId) => {
  try {
    if (!dayObj || !Array.isArray(dayObj.times) || dayObj.times.length === 0) {
      console.log(
        `${LOG_PREFIX} Skipping day (no generated times)`,
        dayObj?.dateString
      );
      return dayObj;
    }

    console.log(
      `${LOG_PREFIX} Querying OrderThrottlingSlot`,
      { storeId, date: dayObj.dateString, generatedTimes: dayObj.times.length }
    );

    const snap = await _firestore
      .collection('OrderThrottlingSlot')
      .where('storeId', '==', storeId)
      .where('date', '==', dayObj.dateString)
      .get();

    if (!snap || snap.empty) {
      console.log(
        `${LOG_PREFIX} No throttling slots found for`,
        dayObj.dateString,
        '→ keeping all times'
      );
      return dayObj;
    }

    const fetchedSlots = [];
    snap.forEach((doc) => fetchedSlots.push({ _id: doc.id, ...doc.data() }));

    const fullSlots = fetchedSlots.filter((s) => isSlotFull(s));

    console.log(`${LOG_PREFIX} Slots fetched for ${dayObj.dateString}`, {
      total: fetchedSlots.length,
      full: fullSlots.length,
      slots: fetchedSlots.map((s) => ({
        id: s._id,
        type: s.type,
        startTime: s.startTime,
        endTime: s.endTime,
        window: `${secondsToHHMM(Number(s.startTime))} - ${secondsToHHMM(
          Number(s.endTime)
        )}`,
        orders: s.orders,
        maxOrders: s.maxOrders,
        volume: s.volume,
        orderVolume: s.orderVolume,
        isFull: isSlotFull(s),
      })),
    });

    if (fullSlots.length === 0) {
      console.log(
        `${LOG_PREFIX} No full slots for ${dayObj.dateString} → keeping all times`
      );
      return dayObj;
    }

    const removed = [];
    const filteredTimes = dayObj.times.filter((t) => {
      const secs = twelveHourToSeconds(t?.displayForUse);
      if (secs === null) return true;
      const blockingSlot = fullSlots.find(
        (s) => Number(s.startTime) <= secs && Number(s.endTime) >= secs
      );
      if (blockingSlot) {
        removed.push({
          time: t.display,
          displayForUse: t.displayForUse,
          slotId: blockingSlot._id,
          slotType: blockingSlot.type,
        });
        return false;
      }
      return true;
    });

    console.log(`${LOG_PREFIX} Filtered times for ${dayObj.dateString}`, {
      before: dayObj.times.length,
      after: filteredTimes.length,
      removedCount: removed.length,
      removed,
    });

    return {
      ...dayObj,
      times: filteredTimes,
      timeGroup: { displayTime: filteredTimes.map((t) => t.display) },
      valueGroup: {
        displayTime: filteredTimes.length ? filteredTimes[0].display : '',
      },
    };
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} filterDay failed for ${dayObj?.dateString} (failing silently)`,
      err
    );
    return dayObj;
  }
};

export const filterThrottledTimeSlots = async (timeSlotDays, storeId) => {
  try {
    if (!Array.isArray(timeSlotDays) || timeSlotDays.length === 0) {
      console.log(`${LOG_PREFIX} No days to filter, skipping`);
      return timeSlotDays;
    }
    if (!storeId) {
      console.log(`${LOG_PREFIX} No storeId provided, skipping`);
      return timeSlotDays;
    }

    console.log(`${LOG_PREFIX} Starting filter`, {
      storeId,
      days: timeSlotDays.map((d) => ({
        date: d.dateString,
        times: d?.times?.length ?? 0,
      })),
    });

    const results = await Promise.all(
      timeSlotDays.map((dayObj) => filterDay(dayObj, storeId))
    );

    const filtered = results.filter(
      (d) => d && Array.isArray(d.times) && d.times.length > 0
    );

    console.log(`${LOG_PREFIX} Finished filter`, {
      daysBefore: timeSlotDays.length,
      daysAfter: filtered.length,
      result: filtered.map((d) => ({
        date: d.dateString,
        times: d.times.length,
      })),
    });

    return filtered;
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} filterThrottledTimeSlots failed (failing silently)`,
      err
    );
    return timeSlotDays;
  }
};

export default filterThrottledTimeSlots;
